/**
 * Reads the listing configuration off a data element record.
 *
 * Items live in `sgbuildex-submittals-data-elements-<env>` and reach this app
 * through `GET /api/data-elements`, which serialises the record's
 * `listingConfig` unchanged:
 *
 *   { dataElementId: "supply_of_precast_components",
 *     submissionMode: "excel-upload",
 *     listingConfig: {
 *       tabName: "Supply of Precast Components",
 *       columns: [
 *         { key: "submittal_id",    label: "Submission ID",   index: 0 },
 *         { key: "submission_date", label: "Submission Date", index: 1,
 *           type: "date", sortable: true }, ... ] } }
 *
 * Only `listingConfig` is read. The item root belongs to other consumers —
 * workflow settings, `type`, `created`, `elementSchema` — so nothing there may
 * be interpreted as a column. An item with no `listingConfig.columns` has no
 * table of its own.
 *
 * `key` is the field on a submittal row, not a UI alias — the listing reads
 * `row[key]` directly.
 *
 * Ported from pitstop-ui (src/pages/submittals/submittals-tab-listing/
 * listing-config.js). The two apps read the same DynamoDB records, so the two
 * copies must stay in step: a config attribute honoured there and ignored here
 * shows up as a column that renders in the submitter's table and not in the
 * consultant's.
 */
import moment from 'moment'

export const V1_DATA_ELEMENT_ID = 'environmental_monthly_submissions'

// Used only if the data element record carries no name of its own.
export const V1_TAB_LABEL = 'Environmental Monthly Submissions'

export const LISTING_PATH = '/submittals'

// Marks the next segment as a data element id rather than a submittal id.
const DE_LISTING = 'de'

/**
 * The listing URL for one data element, which is what selects it now that the
 * tabs live in the sidebar rather than on the page.
 *
 * The `de/` segment is not decoration: `/submittals/:submittalId` already owns
 * the next segment, so `/submittals/<dataElementId>` would resolve to a detail
 * page for a submittal that does not exist. Without an id this is the bare
 * listing, which sends itself on to whichever data element opens first.
 *
 * Pure on purpose — every caller reaches it through `useListingTabPath`, which
 * is where SUBMITTALS_PHASE_2 is applied. A flag-off build must never emit a
 * `de/` URL, since only the tabbed listing can read one.
 */
export const listingPathFor = dataElementId =>
  dataElementId
    ? `${LISTING_PATH}/${DE_LISTING}/${encodeURIComponent(dataElementId)}`
    : LISTING_PATH

/**
 * Whether a path is a data element's listing rather than a submittal's page.
 *
 * Lives here, beside the `listingPathFor` that emits these URLs, so the `de/`
 * segment is spelled out once for both directions.
 *
 * Exact segment, not a bare `startsWith(LISTING_PATH + '/de')`: that also reads
 * `/submittals/dexter-1` as a listing, and the one caller acts on the answer by
 * discarding the rest of the path.
 */
export const isDataElementListing = path =>
  path === `${LISTING_PATH}/${DE_LISTING}` ||
  String(path ?? '').startsWith(`${LISTING_PATH}/${DE_LISTING}/`)

export const FILTER_TYPE = {
  MULTISELECT: 'multiselect',
  DATE_RANGE: 'date-range',
}

// What a picked day is held as between the picker and the request.
export const PICKED_DATE_FORMAT = 'YYYY-MM-DD'

export const COLUMN_TYPE = {
  TEXT: 'text',
  DATE: 'date',
  STATUS_TAG: 'status-tag',
}

const toColumn = (entry, position) => {
  if (!entry || typeof entry !== 'object' || !entry.key) return null

  const key = String(entry.key)
  const isNested = key.includes('.')
  const index = Number(entry.index)
  return {
    key,
    label: String(entry.label ?? key),
    type: entry.type || COLUMN_TYPE.TEXT,
    sortable: entry.sortable === true && !isNested,
    // Number(), not a truthiness check — index 0 is the leftmost column,
    // not a missing one.
    index: Number.isFinite(index) ? index : position,
  }
}

/** Reads a possibly-dotted key off a row. */
export const valueAtKey = (row, key) =>
  String(key)
    .split('.')
    .reduce((value, part) => (value == null ? undefined : value[part]), row)

/**
 * The payload groups the configured columns read from.
 *
 * The API omits `payload` unless the request names the groups wanted, so a
 * column keyed `payload.<group>.<field>` is blank unless `<group>` is asked
 * for. Deriving the list from the columns keeps the two in step.
 */
export const payloadGroupsFromColumns = columns => [
  ...new Set(
    columns
      .map(column => column.key.split('.'))
      .filter(parts => parts[0] === 'payload' && parts.length > 2)
      .map(parts => parts[1])
  ),
]

/**
 * The configured columns as `[{ key, label, type, sortable, index }]`, ordered
 * by index ascending — 0 leftmost.
 */
export const columnsFromConfig = dataElement => {
  const configured = dataElement?.listingConfig?.columns
  if (!Array.isArray(configured)) return []

  return (
    configured
      // Passed explicitly: `map` would supply `toColumn`'s second argument by
      // accident rather than by intent — the `map(parseInt)` trap.
      .map((entry, position) => toColumn(entry, position))
      .filter(column => column !== null)
      .sort((a, b) => a.index - b.index)
  )
}

export const hasListingConfig = dataElement => columnsFromConfig(dataElement).length > 0

export const labelFor = dataElement =>
  dataElement?.listingConfig?.tabName || dataElement?.elementName || dataElement?.dataElementId

/**
 * Reads one entry of `listingConfig.filters`.
 */
const toFilter = (entry, position) => {
  if (!entry || typeof entry !== 'object' || !entry.key) return null

  const isDateRange = entry.type === FILTER_TYPE.DATE_RANGE
  // A filter that cannot say what to send is dropped rather than rendered: a
  // date range needs both bounds, anything else needs its parameter.
  if (isDateRange ? !(entry.paramFrom && entry.paramTo) : !entry.param) return null

  const index = Number(entry.index)
  return {
    key: String(entry.key),
    label: String(entry.label ?? entry.key),
    type: isDateRange ? FILTER_TYPE.DATE_RANGE : FILTER_TYPE.MULTISELECT,
    param: entry.param,
    paramFrom: entry.paramFrom,
    paramTo: entry.paramTo,
    optionsFrom: entry.optionsFrom,
    optionValue: entry.optionValue,
    optionLabel: entry.optionLabel,
    index: Number.isFinite(index) ? index : position,
  }
}

/** The configured filters, ordered by index ascending. */
export const filtersFromConfig = dataElement => {
  const configured = dataElement?.listingConfig?.filters
  if (!Array.isArray(configured)) return []

  return configured
    .map((entry, position) => toFilter(entry, position))
    .filter(Boolean)
    .sort((a, b) => a.index - b.index)
}

/**
 * Turns current selections into request parameters.
 *
 * Two filters may share a parameter — the contract number and name dropdowns
 * both send `project_contract_number` — so values accumulate and are deduped,
 * rather than one filter overwriting the other.
 */
export const buildFilterParams = (filters, selections = {}) => {
  const params = {}

  filters.forEach(filter => {
    const selected = selections[filter.key]

    if (filter.type === FILTER_TYPE.DATE_RANGE) {
      // Sent as instants, resolved here because only the client knows the
      // viewer's zone. Half-open, so `to` advances to the next midnight —
      // add the day before startOf, or a DST shift lands an hour off.
      if (selected?.from) {
        params[filter.paramFrom] = moment(selected.from, PICKED_DATE_FORMAT)
          .startOf('day')
          .toISOString()
      }
      if (selected?.to) {
        params[filter.paramTo] = moment(selected.to, PICKED_DATE_FORMAT)
          .add(1, 'day')
          .startOf('day')
          .toISOString()
      }
      return
    }

    if (!Array.isArray(selected) || selected.length === 0) return
    params[filter.param] = [...new Set([...(params[filter.param] || []), ...selected])]
  })

  return params
}
